import React, { useState, useMemo } from 'react';
import { Table, Icon, Accordion, Header } from 'semantic-ui-react';
import { format } from 'date-fns';
import { Unit } from '~/constants';
import { formatNumber, formatDollars, offtakeBidDecimals, offtakeRateUnit } from '~/utils';
import { normalizeToOverall, normalizeToPerWdc } from '~/utils/calculators/costsFns';
import { ProjectBid } from '~/types/project/ProjectBid';
import BudgetTable from '~/features/projectReview/components/BudgetTable';
import BidHistory from '~/features/projectBidding/shared/BidHistory';
import RfiResponseList from '~/features/requestForInformation/developer/RfiResponseList';
import { usePermissions } from '~/requests/permissions/usePermissions';
import '../styles.scss';

type SortColumn = 'projectName' | 'totalAmount' | 'wdcAmount' | 'kwhAmount' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';
type SemanticSortDirection = 'ascending' | 'descending';

interface PortfolioProjectsBidTableProps {
    projects: any[];
    investorName: string;
    investorId: string;
}

const PortfolioProjectsBidTable: React.FC<PortfolioProjectsBidTableProps> = ({
    projects,
    investorName,
    investorId
}) => {
    const { permissions } = usePermissions();
    const [sortColumn, setSortColumn] = useState<SortColumn>('projectName');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const handleAccordionClick = (e: any, titleProps: any) => {
        const { index } = titleProps;
        const newIndex = activeIndex === index ? -1 : index;
        setActiveIndex(newIndex);
    };


    // Helper function to get system PV size for calculations
    const getSystemPVSizeKwdc = (project: any) => {
        return parseFloat(((project?.projectArrays?.reduce((sum: number, array: any) => sum + (array.sizeKwdc || 0), 0) || 0)).toFixed(3));
    };

    // Helper function to convert bid amounts for sorting
    const getBidAmountForSorting = (project: any, targetUnit: 'TOTAL' | 'WDC' | 'KWH') => {
        const bid = project.projectBids?.[0];
        if (!bid?.amount) return 0;

        const totalSizeKwdc = getSystemPVSizeKwdc(project);
        if (!totalSizeKwdc) return 0;

        if (targetUnit === 'TOTAL') {
            return bid.unit === Unit.WDC
                ? normalizeToOverall(totalSizeKwdc, bid)
                : bid.amount;
        } else {
            return bid.unit === Unit.WDC
                ? bid.amount
                : normalizeToPerWdc(totalSizeKwdc, bid);
        }
    };

    // Helper function to get last updated date for sorting
    const getLastUpdatedForSorting = (project: any) => {
        const bid = project.projectBids?.[0];
        if (!bid?.updatedAt && !bid?.createdAt) {
            return new Date(0); // Use epoch for items without dates to sort them last
        }
        const date = bid?.updatedAt || bid?.createdAt;
        return new Date(date);
    };

    // Sort the data based on current sort column and direction
    const sortedData = useMemo(() => {
        const dataToSort = [...projects];

        return dataToSort.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case 'projectName':
                    aValue = (a.name || 'Unknown Project').toLowerCase();
                    bValue = (b.name || 'Unknown Project').toLowerCase();
                    break;
                case 'totalAmount':
                    aValue = getBidAmountForSorting(a, 'TOTAL');
                    bValue = getBidAmountForSorting(b, 'TOTAL');
                    break;
                case 'wdcAmount':
                    aValue = getBidAmountForSorting(a, 'WDC');
                    bValue = getBidAmountForSorting(b, 'WDC');
                    break;
                case 'kwhAmount':
                    aValue = getBidAmountForSorting(a, 'KWH');
                    bValue = getBidAmountForSorting(b, 'KWH');
                    break;
                case 'lastUpdated':
                    aValue = getLastUpdatedForSorting(a);
                    bValue = getLastUpdatedForSorting(b);
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [projects, sortColumn, sortDirection]);

    // Handle column header click
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            // If clicking the same column, toggle direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // If clicking a different column, set new column and default to ascending
            setSortColumn(column);
            if (column === 'projectName') {
                setSortDirection('asc');
            } else {
                setSortDirection('desc');
            }
        }
    };

    // Convert sort direction to Semantic UI format
    const getSemanticSortDirection = (column: SortColumn): SemanticSortDirection | undefined => {
        if (sortColumn !== column) return undefined;
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    };

    // Helper function to convert bid amounts between units
    const convertBidAmount = (project: any, targetUnit: 'TOTAL' | 'WDC' | 'KWH') => {
        const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
        let totalSizeKwdc = getSystemPVSizeKwdc(project);

        if (!bid?.amount || !totalSizeKwdc) return 0;

        if (targetUnit === 'TOTAL') {
            return bid.unit === Unit.WDC
                ? normalizeToOverall(totalSizeKwdc, bid)
                : bid.amount;
        } else {
            return bid.unit === Unit.WDC
                ? bid.amount
                : normalizeToPerWdc(totalSizeKwdc, bid);
        }
    };

    // Helper function to format bid amounts for display
    const formatBidAmount = (project: any, displayUnit: 'TOTAL' | 'WDC' | 'KWH') => {
        const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
        const isDeclined = project?.status === 'BID_REJECTED' || project?.status === 'DECLINED_TO_BID';
        if (!bid?.amount) {
            return <i style={{ color: '#5a5a5a' }}>{isDeclined ? 'Declined' : 'Awaiting bid...'}</i>;
        }

        if (displayUnit === 'KWH') {
            const offtakeRate = bid.amount;
            return `$${formatNumber(offtakeRate, offtakeBidDecimals(project?.contractType))} / ${offtakeRateUnit(project?.contractType)}`;
        }

        let totalSizeKwdc = getSystemPVSizeKwdc(project);
        if (!totalSizeKwdc) {
            return <i style={{ color: '#5a5a5a' }}>-</i>;
        }

        const convertedAmount = convertBidAmount(project, displayUnit);
        const decimals = displayUnit === 'WDC' ? 3 : 0;
        return formatDollars(convertedAmount, { dec: decimals });
    };

    const formatLastUpdated = (project: any) => {
        const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
        const isDeclined = project?.status === 'DECLINED_TO_BID' || project?.status === 'BID_REJECTED';
        const date = bid?.createdAt ?? (isDeclined ? project?.updatedAt : null);
        if (!date) {
            return <i style={{ color: '#5a5a5a' }}>—</i>;
        }
        return format(new Date(date), 'MMM d, yyyy');
    };

    const getProjectName = (project: any) => {
        return project.name || 'Unknown Project';
    };

    const getProjectPricingType = (project: any) => {
        return project.pricingType;
    };

    return (
        <div className="bid-table-container">
            <Table compact responsive sortable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell
                            width={4}
                            sorted={getSemanticSortDirection('projectName')}
                            onClick={() => handleSort('projectName')}
                            style={{ cursor: 'pointer' }}
                        >
                            Project
                        </Table.HeaderCell>
                        {projects?.[0]?.pricingType === 'OFFTAKE' ? (
                            <Table.HeaderCell
                                width={2}
                                textAlign="right"
                                sorted={getSemanticSortDirection('kwhAmount')}
                                onClick={() => handleSort('kwhAmount')}
                                style={{ cursor: 'pointer' }}
                            >
                                Bid ($/{offtakeRateUnit(projects?.[0]?.contractType)})
                            </Table.HeaderCell>
                        ) : null}
                        {projects?.[0]?.pricingType === 'ACQUISITION' || projects?.[0]?.pricingType === 'DEV_FEE' ? (
                            <>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('wdcAmount')}
                                    onClick={() => handleSort('wdcAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Bid ($/Wdc)
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('totalAmount')}
                                    onClick={() => handleSort('totalAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Bid (Total)
                                </Table.HeaderCell>
                            </>
                        ) : null}
                        <Table.HeaderCell
                            width={2}
                            textAlign="right"
                            sorted={getSemanticSortDirection('lastUpdated')}
                            onClick={() => handleSort('lastUpdated')}
                            style={{ cursor: 'pointer' }}
                        >
                            Last updated
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {sortedData?.map((project: any, index: number) => {
                        const bid = project.projectBids?.find((bid: ProjectBid) => bid.bidStatus === 'PENDING') as ProjectBid;
                        const isAccordionOpen = activeIndex === index;
                        const pricingType = getProjectPricingType(project);

                        return (
                            <React.Fragment key={project?.id || index}>
                                <Table.Row className="bid-row">
                                    <Table.Cell>
                                        <Accordion>
                                            <Accordion.Title
                                                active={isAccordionOpen}
                                                index={index}
                                                onClick={handleAccordionClick}
                                                className="accordion-investor-row"
                                            >
                                                <Icon name='dropdown' style={{ marginRight: '8px' }} />
                                                <div className="investor-info">
                                                    <span style={{ fontWeight: 'bold' }}>
                                                        {getProjectName(project)}
                                                    </span>
                                                </div>
                                            </Accordion.Title>
                                        </Accordion>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        {pricingType === 'OFFTAKE'
                                            ? formatBidAmount(project, 'KWH')
                                            : formatBidAmount(project, 'WDC')
                                        }
                                    </Table.Cell>
                                    {pricingType === 'ACQUISITION' || pricingType === 'DEV_FEE' ? (
                                        <Table.Cell textAlign="right">
                                            {formatBidAmount(project, 'TOTAL')}
                                        </Table.Cell>
                                    ) : null}
                                    <Table.Cell textAlign="right">
                                        {formatLastUpdated(project)}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row className="bid-row-accordion" style={{ display: isAccordionOpen ? 'table-row' : 'none' }}>
                                    <Table.Cell colSpan={4}>
                                        <div className="accordion-content">
                                            {project.pricingType !== 'OFFTAKE' ? (
                                                <>
                                                    <Header as="h5">Budget Table</Header>
                                                    <BudgetTable project={project} formState={bid} />
                                                </>
                                            ) : null}


                                            {project.rfiResponses && project.rfiResponses.length > 0 ? (
                                                <>
                                                    <Header as="h5">Supporting Information</Header>
                                                    <RfiResponseList
                                                        rfiResponses={project.rfiResponses || []}
                                                        investorName={investorName}
                                                        tableView={true}
                                                    />
                                                </>
                                            ) : null}

                                            {permissions?.isAdmin ? (
                                                <>
                                                    <Header as="h5">Bid History</Header>
                                                    <BidHistory project={project} investorId={investorId} />
                                                </>
                                            ) : null}
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            </React.Fragment>
                        );
                    })}
                </Table.Body>
            </Table>

            {projects?.length === 0 && (
                <div className="empty-state">
                    <Icon name="hourglass half" size="large" />
                    <h3>No projects available</h3>
                    <p>No project bids from this investor yet.</p>
                </div>
            )}
        </div>
    );
};

export default PortfolioProjectsBidTable;
